import {computed, Signal, signal, WritableSignal} from '@angular/core';
import {isValidPhoneNumber} from '@utils';

export type SignalValidator<T> = {
	validate: (value: T) => boolean;
	validMessage?: string;
	invalidMessage?: string;
};

export type SignalForm<T> = {
	value$: WritableSignal<T>;
	validators: Record<string, SignalValidator<T>>;
	[key: string]: unknown;
};

export type SignalFormCtor<T> = SignalForm<T> & {
	addValidator: (
		validators: Record<string, SignalValidator<T>>,
		options?: Partial<{
			clearOtherValidators?: boolean;
		}>,
	) => void;
	clearValidators: () => void;
	isValid$: Signal<boolean>;
};

export function signalForm<T>(
	initialValue: T,
	validatorsFactory: (signalForm: SignalForm<T>) => Record<string, SignalValidator<T>> = () => ({}),
	props: Record<string, unknown> = {},
): SignalFormCtor<T> {
	const signalFrom$ = {
		value$: signal(initialValue),
		validators: validatorsFactory({value$: signal(initialValue), validators: {}}),
	};

	Object.assign(signalFrom$, props);

	const isValid$ = computed(() => {
		const value = signalFrom$.value$();
		const validators = Object.values(signalFrom$.validators);
		return validators.every(validator => validator.validate(value));
	});

	const addValidator = (
		validators: Record<string, SignalValidator<T>>,
		options: {
			clearOtherValidators?: boolean;
		} = {clearOtherValidators: false},
	) => {
		if (options.clearOtherValidators) {
			clearValidators();
		}
		Object.assign(signalFrom$.validators, validators);
	};

	const clearValidators = () => {
		Object.keys(signalFrom$.validators).forEach(key => delete signalFrom$.validators[key]);
	};
	return Object.assign(signalFrom$, {addValidator, clearValidators, isValid$});
}

/* -------------------------------------------------------------------------------------------------------------- */
export function emailSignalValidator<T extends string = string>(): SignalValidator<T> {
	const emailRegex = new RegExp('[^@\\s]+@[^@\\s]+\\.[^@\\s]+');
	return {
		validate: (value: T) => emailRegex.test(value),
		invalidMessage: 'struct.user-edit.errors.invalidemail',
	};
}

/* -------------------------------------------------------------------------------------------------------------- */
export function phoneNumberSignalValidator<T extends string = string>(): SignalValidator<T> {
	return {
		validate: (value: T) => isValidPhoneNumber(value),
		invalidMessage: 'struct.user-edit.errors.invalidphone',
	};
}
/* -------------------------------------------------------------------------------------------------------------- */
